// Coin pickups with v1's spawn patterns (js/obstacle-manager.js:346-380):
// rows of 3/5 on a pavement lane, or a jump arc. Phase 7: all coins render
// as ONE InstancedMesh (single draw call) with per-frame matrix updates —
// they were individual pooled meshes before.

import * as THREE from 'three';
import { LANES, SPAWN_DISTANCE, COIN_SPACING, COIN_MAGNET } from '@/config/tuning';
import type { Rng } from '@/core/rng';

export interface Coin {
  position: THREE.Vector3;
  spin: number;
}

const COIN_CAPACITY = 96;

export class CoinSystem {
  readonly coins: Coin[] = [];
  private readonly mesh: THREE.InstancedMesh;
  private readonly dummy = new THREE.Object3D();

  constructor(
    scene: THREE.Scene,
    private readonly rng: Rng,
  ) {
    const geo = new THREE.CylinderGeometry(0.22, 0.22, 0.05, 16);
    geo.rotateX(Math.PI / 2);
    const mat = new THREE.MeshLambertMaterial({ color: 0xffd700, emissive: 0x996f00 });
    this.mesh = new THREE.InstancedMesh(geo, mat, COIN_CAPACITY);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false; // Instances span the whole run corridor
    scene.add(this.mesh);
  }

  spawnPattern(): void {
    const laneX = this.rng.next() < 0.5 ? LANES[0] : LANES[3];
    const roll = this.rng.next();
    if (roll < 0.35) {
      for (let i = 0; i < 3; i++) this.spawnAt(laneX, 0.5, SPAWN_DISTANCE - i * COIN_SPACING);
    } else if (roll < 0.6) {
      for (let i = 0; i < 5; i++) this.spawnAt(laneX, 0.5, SPAWN_DISTANCE - i * COIN_SPACING);
    } else {
      // Jump arc (v1 :370)
      const arcHeights = [0.5, 1.2, 1.7, 1.2, 0.5];
      for (let i = 0; i < arcHeights.length; i++) {
        this.spawnAt(laneX, arcHeights[i] ?? 0.5, SPAWN_DISTANCE - i * COIN_SPACING);
      }
    }
  }

  private spawnAt(x: number, y: number, z: number): void {
    if (this.coins.length >= COIN_CAPACITY) return;
    this.coins.push({ position: new THREE.Vector3(x, y, z), spin: this.rng.next() * Math.PI });
  }

  /** Scroll + coin-magnet attraction (v1 COIN_MAGNET_CONFIG), then write all
   *  instance matrices in one pass. */
  update(dt: number, gameSpeed: number, playerPos: THREE.Vector3, magnetMultiplier: number): void {
    const dz = gameSpeed * dt * 60;
    const radius = COIN_MAGNET.RADIUS * magnetMultiplier;

    for (let i = this.coins.length - 1; i >= 0; i--) {
      const coin = this.coins[i];
      if (!coin) continue;
      coin.position.z += dz;

      const distance = coin.position.distanceTo(playerPos);
      if (distance < radius && distance > 0.01) {
        const closeness = 1 - distance / radius;
        const speed = COIN_MAGNET.ATTRACTION_SPEED * (1 + closeness * COIN_MAGNET.ACCELERATION);
        coin.position.lerp(playerPos, Math.min((speed * dt) / distance, 1));
        coin.spin += 2 * COIN_MAGNET.SPIN_MULTIPLIER * dt;
      } else {
        coin.spin += 2 * dt;
      }
      if (coin.position.z > 15) this.despawn(i);
    }

    for (let i = 0; i < this.coins.length; i++) {
      const coin = this.coins[i];
      if (!coin) continue;
      this.dummy.position.copy(coin.position);
      this.dummy.rotation.set(0, 0, coin.spin);
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(i, this.dummy.matrix);
    }
    this.mesh.count = this.coins.length;
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  despawn(index: number): void {
    this.coins.splice(index, 1);
  }

  clear(): void {
    this.coins.length = 0;
    this.mesh.count = 0;
  }
}
